import AWS from 'aws-sdk';
// eslint-disable-next-line import/extensions
import { packToBlob } from 'ipfs-car/pack/blob';
import uint8arrays from 'uint8arrays';
import hash from 'hash.js';
import getConfig from '../config';

const { ipfsGateway } = getConfig();

const FILEBASE_ACCESS_KEY = process.env.REACT_APP_FILEBASE_ACCESS_KEY || '';
const FILEBASE_SECRET_KEY = process.env.REACT_APP_FILEBASE_SECRET_KEY || '';
const FILEBASE_BUCKET = process.env.REACT_APP_FILEBASE_BUCKET || '';
const FILEBASE_REGION = process.env.REACT_APP_FILEBASE_REGION || 'us-east-1';
const FILEBASE_S3_ENDPOINT = process.env.REACT_APP_FILEBASE_S3_ENDPOINT || 'https://s3.filebase.com';

const ensureFilebaseConfig = () => {
  if (!FILEBASE_ACCESS_KEY || !FILEBASE_SECRET_KEY || !FILEBASE_BUCKET) {
    throw new Error('Missing Filebase env vars: REACT_APP_FILEBASE_ACCESS_KEY/SECRET_KEY/BUCKET');
  }
};

const createFilebaseClient = () => new AWS.S3({
  region: FILEBASE_REGION,
  endpoint: FILEBASE_S3_ENDPOINT,
  credentials: {
    accessKeyId: FILEBASE_ACCESS_KEY,
    secretAccessKey: FILEBASE_SECRET_KEY,
  },
  s3ForcePathStyle: true,
  signatureVersion: 'v4',
});

const normalizeToUint8Array = async (data) => {
  if (!data) return new Uint8Array();
  if (typeof data === 'string') {
    return uint8arrays.fromString(data);
  }
  if (data instanceof Blob) {
    const buffer = await data.arrayBuffer();
    return new Uint8Array(buffer);
  }
  if (data instanceof Uint8Array) {
    return data;
  }
  if (data?.buffer) {
    return new Uint8Array(data.buffer);
  }
  return uint8arrays.fromString(JSON.stringify(data));
};

const uploadCarToFilebase = async (client, car, cid) => {
  const key = `ipfs/${cid}.car`;
  await client.putObject({
    Bucket: FILEBASE_BUCKET,
    Key: key,
    Body: car,
    ContentType: 'application/vnd.ipld.car',
    Metadata: {
      import: 'car',
    },
  }).promise();
  return cid;
};

const isBytes = (value) => ArrayBuffer.isView(value) || value instanceof ArrayBuffer;

const isBlob = (value) => typeof Blob !== 'undefined' && value instanceof Blob;

const isFileLike = (value) => value && typeof value === 'object' && (value.path || value.content);

const normalizeAddAllInput = (items) => {
  if (!items) return [];
  if (typeof items === 'string' || items instanceof String || isBytes(items) || isBlob(items)) {
    return [{ path: 'file', content: items }];
  }
  if (isFileLike(items)) {
    const path = items.path || items.path === '' ? items.path : 'file';
    return [{ ...items, path: path || 'file' }];
  }
  if (Array.isArray(items)) {
    return items.map((item) => {
      if (!isFileLike(item)) return item;
      const path = item.path || item.path === '' ? item.path : 'file';
      return { ...item, path: path || 'file' };
    });
  }
  if (items[Symbol.iterator] || items[Symbol.asyncIterator]) return items;
  return [items];
};

const packAndUpload = async (client, input, options = {}) => {
  const { root, car } = await packToBlob({ input, ...options });
  const cid = root.toString();
  await uploadCarToFilebase(client, car, cid);
  return cid;
};

const normalizeGateway = (gateway) => {
  if (!gateway) return '';
  return gateway
    .replace(/\/ipfs\/?$/i, '')
    .replace(/\/+$/g, '');
};

const normalizeCid = (cid) => {
  if (!cid) return '';
  let value = cid.trim();
  value = value.replace(/^ipfs:\/\//i, '');
  if (value.includes('/ipfs/')) {
    const parts = value.split('/ipfs/');
    value = parts[parts.length - 1];
  }
  value = value.replace(/^\/?ipfs\//i, '');
  value = value.replace(/^\/+/, '');
  return value;
};

export const buildGatewayUrl = (cid, path = '') => {
  const cleanCid = normalizeCid(cid);
  if (!cleanCid) return '';
  const gateway = normalizeGateway(ipfsGateway);
  if (!gateway) return '';
  const safePath = path ? `/${path.replace(/^\/+/, '')}` : '';
  return `${gateway}/ipfs/${cleanCid}${safePath}`;
};

export const initIPFS = async () => {
  const client = createFilebaseClient();

  return {
    add: async (data, options = {}) => {
      ensureFilebaseConfig();
      const content = await normalizeToUint8Array(data);
      const cid = await packAndUpload(client, content, { wrapWithDirectory: false, ...options });
      return { path: cid, cid: { string: cid } };
    },
    async* addAll(items = [], options = {}) {
      ensureFilebaseConfig();
      const normalizedItems = normalizeAddAllInput(items);
      if (!normalizedItems || !normalizedItems.length) return;
      const { wrapWithDirectory = true, ...rest } = options || {};
      const cid = await packAndUpload(client, normalizedItems, { wrapWithDirectory, ...rest });
      yield { path: '', cid: { string: cid } };
    },
  };
};

const fetchGateway = async (cid, path = '', signal) => {
  const url = buildGatewayUrl(cid, path);
  if (!url) return null;
  const response = await fetch(url, { signal });
  if (!response.ok) return null;
  return response;
};

const normalizeListingPath = (href, cid) => {
  if (!href) return null;
  const trimmed = href.trim();
  if (!trimmed || trimmed === '#' || trimmed.startsWith('#')) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return null;
  if (trimmed.startsWith('ipfs://') || /^(?:mailto:|javascript:)/i.test(trimmed)) {
    return null;
  }
  if (trimmed.includes('://') || trimmed.includes('http:') || trimmed.includes('https:')) return null;
  const clean = trimmed.split('?')[0].replace(/^\/+/, '');
  if (!clean || clean === '..' || clean === '../') return null;
  const withoutPrefix = clean.replace(new RegExp(`^(ipfs/)?${cid}/?`), '');
  const normalized = withoutPrefix.replace(/\/$/, '');
  if (normalized.includes('http:') || normalized.includes('https:')) return null;
  if (!normalized || normalized === cid) return null;
  if (normalized.includes('ipfs/')) return null;
  if (normalized === `ipfs/${cid}` || normalized.startsWith(`ipfs/${cid}/`)) return null;
  if (normalized === `${cid}` || normalized.startsWith(`${cid}/`)) return null;
  return decodeURIComponent(normalized);
};

const parseDirectoryListing = (html, cid) => {
  if (typeof DOMParser === 'undefined' || !html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const links = Array.from(doc.querySelectorAll('a'));
  const paths = links
    .map((link) => normalizeListingPath(link.getAttribute('href'), cid))
    .filter(Boolean);
  return Array.from(new Set(paths));
};

export const getJSONFileFromIpfs = async (_client, cid, signal) => {
  try {
    const response = await fetchGateway(cid, '', signal);
    if (!response) return false;
    const text = await response.text();
    return JSON.parse(text);
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getRawJSONFileFromIpfs = async (_client, cid, signal) => {
  try {
    const response = await fetchGateway(cid, '', signal);
    if (!response) return false;
    return await response.text();
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getFilesFromDirectory = async (_client, cid, signal) => {
  try {
    const response = await fetchGateway(cid, '', signal);
    if (!response) return [];
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('text/html')) {
      return [];
    }
    const text = await response.text();
    const cleanCid = normalizeCid(cid);
    const paths = parseDirectoryListing(text, cleanCid);
    return paths.map((path) => ({ path }));
  } catch (e) {
    console.log(e);
    return [];
  }
};

export const getFilesFromDirWithContent = async (_client, cid, signal) => {
  const updateArray = [];
  const files = await getFilesFromDirectory(null, cid, signal);
  if (!files.length) {
    const response = await fetchGateway(cid, '', signal);
    if (!response) return [];
    const buffer = await response.arrayBuffer();
    return [{ path: '', content: [new Uint8Array(buffer)] }];
  }
  for (const file of files) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchGateway(cid, file.path, signal);
    // eslint-disable-next-line no-continue
    if (!response) continue;
    // eslint-disable-next-line no-await-in-loop
    const buffer = await response.arrayBuffer();
    updateArray.push({ path: file.path, content: [new Uint8Array(buffer)] });
  }
  return updateArray;
};

export const getReferenceHash = async (cid) => {
  const ipfs = await initIPFS();
  const res = await getRawJSONFileFromIpfs(ipfs, cid);
  if (res) {
    const objJson256 = hash.sha256().update(res).digest();
    const objJsonB64 = Buffer.from(objJson256).toString('base64');
    return objJsonB64;
  }
  return null;
};

export const getMediaHash = async (cid) => {
  const ipfs = await initIPFS();
  const res = await getFilesFromDirWithContent(ipfs, cid);
  if (res && res[0]?.content) {
    const objJson256 = hash.sha256().update(res[0].content).digest();
    return Buffer.from(objJson256).toString('base64');
  }
  return null;
};
